import { useRemoteClientGet, useRemoteClientPost, useRemoteClientPut } from '@/configs/http-client'
import { Endpoints } from '@/remote/endpoints'
import { useLocalStorage } from '@/remote/local-storage'
import { zodResolver } from '@hookform/resolvers/zod'
import { useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import { useSearchParams } from 'react-router-dom'
import Select from 'react-select'
import { useHookFormMask } from 'use-mask-input'
import { CardInfoSelectMember } from '../components/card-info-select-member'
import { ComboSelect } from '../components/combo-select'
import { ComboSelectWithLabel } from '../components/combo-select-with-label'
import { CustomSelect } from '../components/custom-select'
import { Button, Input } from '../components/ui/components'
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '../components/ui/form'
import { SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select'
import { Textarea } from '../components/ui/textarea'
import { useToast } from '../hooks'
import { usePromiseToast } from '../hooks/toast-promisse'
import { convertParamsToJson, useCashfitParams } from '../hooks/use-params'
import { convertDateDisplayFormat } from '../utils/formatDateDisplay'
import { formatDateSubmit } from '../utils/formatDateSubmit'
import { formatPhone, removeNonNumeric } from '../utils/formatPhone'
import { formatParams } from '../utils/pagination'
import { states } from '../utils/states'
import { MemberGymFormData, memberSchema, Student } from '../validators/member-schema-validator'

type MemberGymFormProps = {
  id: string
}

export default function MemberGymForm({ id }: MemberGymFormProps) {
  const { httpClientPost } = useRemoteClientPost()
  const { remoteClientGet } = useRemoteClientGet()
  const { httpClientPut } = useRemoteClientPut()
  const { triggerToast } = usePromiseToast()
  const [params] = useSearchParams()
  const { getUnitId } = useLocalStorage()
  const { showToast } = useToast()

  const [dataConsult, setDataConsult] = useState<any>([])
  //const [loading, setLoading] = useState(true)

  const [selectedModalities, setSelectedModalities] = useState<any>([])

  const isEditMode = Boolean(id)

  // nome, cpf , rg, email, data nascimento, sexo, estado civil, consultor e unidade

  const modality = [
    { value: 'MUSCULACAO', label: 'Musculação' },
    { value: 'FUTEBOL', label: 'Futebol' },
    { value: 'BASQUETE', label: 'Basquete' },
    { value: 'VOLEI', label: 'Vôlei' },
    { value: 'NATACAO', label: 'Natação' },
    { value: 'TENIS', label: 'Tênis' },
    { value: 'ATLETISMO', label: 'Atletismo' },
    { value: 'CICLISMO', label: 'Ciclismo' },
    { value: 'ARTES_MARCIAIS', label: 'Artes Marciais' },
    { value: 'DANCA', label: 'Dança' },
    { value: 'OUTROS', label: 'Outros' }
  ]

  const form = useForm<MemberGymFormData>({
    resolver: zodResolver(memberSchema),
    defaultValues: {
      id: undefined,
      nome: '',
      foto: '',
      cpf: '',
      rg: '',
      email: '',
      dataNascimento: '',
      sexo: undefined,
      estadoCivil: undefined,
      profissao: undefined,
      dataVencimentoExameMedico: '',
      status: 1,
      consultor: {
        id: 0,
        nome: '',
        email: ''
      },
      endereco: {
        numero: '',
        bairro: '',
        cidade: '',
        estado: '',
        cep: '',
        rua: ''
      },
      telefoneCelular: {
        numero: '',
        tipo: 'CELULAR'
      },
      dadosEmergencia: {
        contato: '',
        telefone: ''
      },
      responsavel: {
        nome: '',
        telefone: '',
        documento: ''
      },
      observacao: '',
      informacoesAdicionais: {
        objetivoPrincipal: '',
        observacoes: ''
      },
      responsabilidade: {
        indicacao: '',
        consultor: '',
        professor: ''
      },
      unidade: {
        id: getUnitId() || params.get('unit') || 0
      }
    }
  })

  const registerWithMask = useHookFormMask(form.register)

  const getStudent = async (id: number) => {
    try {
      const response = await remoteClientGet(Endpoints.students + `/${id}`)
      const data = response?.data?.data || ({} as Student)
      const dataFormat = {
        ...data,
        id: data.id,
        nome: data.nome || '',
        foto: data.foto || '',
        cpf: data.cpf || '',
        rg: data.rg || '',
        email: data.email || '',
        dataNascimento: data.dataNascimento ? convertDateDisplayFormat(data.dataNascimento.split('T')[0]) : '',
        sexo: data.sexo || undefined,
        estadoCivil: data.estadoCivil || undefined,
        profissao: data.profissao || undefined,
        dataVencimentoExameMedico: data.dataVencimentoExameMedico ? convertDateDisplayFormat(data.dataVencimentoExameMedico.split('T')[0]) : '',
        status: data.status || 1,
        consultor: {
          id: data.consultor?.id || 0,
          nome: data.consultor?.nome || '',
          email: data.consultor?.email || ''
        },
        endereco: {
          numero: data.endereco?.numero || '',
          bairro: data.endereco?.bairro || '',
          cidade: data.endereco?.cidade || '',
          estado: data.endereco?.estado || '',
          cep: data.endereco?.cep || '',
          rua: data.endereco?.rua || '',
          complemento: data.endereco?.complemento || ''
        },
        telefoneCelular: {
          numero: formatPhone(`${data.telefone[0]?.ddd}${data.telefone[0]?.numero}`) || '',
          tipo: 'CELULAR'
        },
        telefoneResidencial: {
          numero: formatPhone(`${data.telefone[1]?.ddd}${data.telefone[1]?.numero}`) || '',
          tipo: 'RESIDENCIAL'
        },
        dadosEmergencia: {
          contato: data.dadosEmergencia[0]?.contato || '',
          telefone: data.dadosEmergencia[0]?.telefone || ''
        },
        responsavel: {
          nome: data.responsavel?.nome || '',
          telefone: data.responsavel?.telefone || '',
          documento: data.responsavel?.documento || ''
        },
        observacao: data.observacao || '',
        informacoesAdicionais: {
          objetivoPrincipal: data.informacoesAdicionais?.objetivoPrincipal || '',
          observacoes: data.informacoesAdicionais?.observacoes || ''
        },
        responsabilidade: {
          indicacao: data.responsabilidade?.indicacao || '',
          consultor: data.responsabilidade?.consultor || '',
          professor: data.responsabilidade?.professor || ''
        },
        unidade: {
          id: getUnitId() || params.get('unit') || 0
        }
      }

      const modalitiesStudant = data.informacoesAdicionais?.modalidadesInteresse || []

      const modalitiesSelect = modality.filter((item: any) => modalitiesStudant.includes(item.value))

      setSelectedModalities(modalitiesSelect)

      form.reset(dataFormat)
      form.setValue('endereco.bairro', dataFormat.endereco.bairro || '')
      form.setValue('endereco.rua', dataFormat.endereco.rua || '')

      //setLoading(false)
    } catch (error: any) {
      //setLoading(true)
      console.error(error)
    }
  }

  const getConsultants = async () => {
    try {
      const response = await remoteClientGet(
        Endpoints.consultant,
        formatParams({
          page: 0,
          size: 100,
          sortParam: 'nome',
          order: 'desc',
          idUnidade: getUnitId() || params.get('unit') || 0
        })
      )
      const data = response?.data?.data?.content || []
      setDataConsult(
        data.map((item: any) => ({
          ...item,
          value: item.id,
          label: item.nome
        }))
      )
    } catch (error: any) {
      console.error(error)
    }
  }

  const onSubmit = async (data: any) => {
    console.debug('Form data:', data)
    const dataFormat = {
      ...data,
      foto: 'base64encodedstring',
      cpf: removeNonNumeric(data.cpf),
      dataNascimento: formatDateSubmit(data.dataNascimento),
      dataVencimentoExameMedico: formatDateSubmit(data.dataVencimentoExameMedico),
      telefone: [],
      dadosEmergencia: [],
      informacoesAdicionais: {
        modalidadesInteresse: selectedModalities.map((item: any) => item.value),
        objetivoPrincipal: data.informacoesAdicionais.objetivoPrincipal,
        observacoes: data.informacoesAdicionais.observacoes
      }
    }

    if (data.telefoneCelular.numero) {
      dataFormat.telefone.push({
        numero: removeNonNumeric(data.telefoneCelular.numero).slice(2, 11).trim(),
        ddd: removeNonNumeric(data.telefoneCelular.numero).slice(0, 2).trim(),
        tipo: 'CELULAR'
      })
    }

    if (data.telefoneResidencial.Numero) {
      dataFormat.telefone.push({
        numero: removeNonNumeric(data.telefoneResidencial.Numero).slice(2, 11).trim(),
        ddd: removeNonNumeric(data.telefoneCelular.numero).slice(0, 2).trim(),
        tipo: 'RESIDENCIAL'
      })
    }

    if (data.dadosEmergencia.telefone && data.dadosEmergencia.contato) {
      console.log('Dados Emergencia', data.dadosEmergencia)
      dataFormat.dadosEmergencia.push({
        telefone: data.dadosEmergencia.telefone,
        contato: data.dadosEmergencia.contato
      })
    }

    delete dataFormat.telefoneCelular
    delete dataFormat.telefoneResidencial
    console.debug({ dataFormat })
    try {
      //console.log('Data', data)
      if (isEditMode) {
        await httpClientPut(Endpoints.students, { ...dataFormat })
      } else {
        delete dataFormat.id
        await httpClientPost(Endpoints.students, { ...dataFormat })
      }
      showToast('Requisição Enviada', 'success')
      form.reset()
      triggerToast(getStudent(Number(id)), {
        loading: 'Carregando dados...',
        success: 'Dados carregados',
        error: 'Erro ao carregar dados.'
      })
    } catch (error: any) {
      console.error('Erro no envio:', error?.response?.data?.errors)
      const errors = error?.response?.data?.errors
      if (errors) {
        for (const key in errors) {
          showToast(errors[key], 'error')
        }
      }
      showToast('Erro ao enviar requisição', 'error')
    }
  }

  useEffect(() => {
    getConsultants()
  }, [])

  useEffect(() => {
    if (isEditMode) {
      triggerToast(getStudent(Number(id)), {
        loading: 'Carregando dados...',
        success: 'Dados carregados',
        error: 'Erro ao carregar dados.'
      })
    }
  }, [id])

  // useEffect(() => {
  //   console.log('Selecionados')
  //   console.log(selectedModalities)
  // },[selectedModalities])

  const formValues = form.watch()

  useEffect(() => {
    if (/^\d{5}-\d{3}$/.test(formValues?.endereco?.cep.trim()) && !id) {
      const cep = formValues.endereco.cep
      remoteClientGet('/cep/' + cep)
        .then((data) => {
          const response = data.data.data
          form.setValue('endereco.bairro', response?.bairro || '')
          form.setValue('endereco.rua', response?.logradouro || '')
          form.setValue('endereco.complemento', response?.complemento || '')
          form.setValue('endereco.cidade', response?.localidade || '')
          form.setValue('endereco.estado', response?.uf)
        })
        .catch((error) => {
          console.error(error)
        })
    }
  }, [formValues?.endereco?.cep])

  useEffect(() => {
    console.log(formValues)
  }, [formValues])

  const {
    formState: { errors }
  } = form

  useEffect(() => {
    console.log('Erros do formulário:', errors)
  }, [errors])

  const { setParams } = useCashfitParams(convertParamsToJson(params))
  const backToStudents = () => setParams({ tabSelected: 'studants' as any })

  if (!params.get('tabContentId')) return <CardInfoSelectMember backToStudents={backToStudents} />

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="flex flex-col gap-3 h-[calc(100vh-190px)] overflow-y-auto px-1">
        <FormField
          control={form.control}
          name="nome"
          render={({ field }) => (
            <FormItem>
              <FormLabel>
                <span className="text-gray-400">*</span> Nome Completo
              </FormLabel>
              <FormControl>
                <Input {...field} placeholder="Nome completo" />
              </FormControl>
              <FormMessage className="text-xs">{form.formState.errors.nome?.message}</FormMessage>
            </FormItem>
          )}
        />

        <div className="grid grid-cols-3 gap-3 w-full">
          <FormField
            control={form.control}
            name="cpf"
            render={({ field }) => (
              <FormItem>
                <FormLabel>
                  <span className="text-gray-400">*</span> CPF
                </FormLabel>
                <FormControl>
                  <Input
                    {...field}
                    {...registerWithMask('cpf', 'cpf', {
                      required: true
                    })}
                    placeholder="000.000.000-00"
                  />
                </FormControl>
                <FormMessage className="text-xs">{form.formState.errors.cpf?.message}</FormMessage>
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="rg"
            render={({ field }) => (
              <FormItem>
                <FormLabel>
                  <span className="text-gray-400">*</span> RG
                </FormLabel>
                <FormControl>
                  <Input {...field} {...registerWithMask('rg', ['99999999'])} placeholder="RG..." />
                </FormControl>
                <FormMessage className="text-xs">{form.formState.errors.rg?.message}</FormMessage>
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="dataNascimento"
            render={({ field }) => (
              <FormItem>
                <FormLabel>
                  <span className="text-gray-400">*</span> Data de Nascimento
                </FormLabel>
                <FormControl>
                  <Input {...field} {...registerWithMask('dataNascimento', '99/99/9999')} placeholder="dd/mm/yyyy" />
                </FormControl>
                <FormMessage className="text-xs">{form.formState.errors.dataNascimento?.message}</FormMessage>
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="dataVencimentoExameMedico"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Vencimento do exame médico</FormLabel>
                <FormControl>
                  <Input {...field} {...registerWithMask('dataVencimentoExameMedico', '99/99/9999')} placeholder="dd/mm/yyyy" />
                </FormControl>
                <FormMessage className="text-xs">{form.formState.errors.dataVencimentoExameMedico?.message}</FormMessage>
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="sexo"
            render={({ field }) => (
              <FormItem>
                <FormLabel>
                  <span className="text-gray-400">*</span> Sexo
                </FormLabel>
                <CustomSelect onValueChange={field.onChange} defaultValue={field.value ?? ''}>
                  <FormControl>
                    <SelectTrigger>
                      <SelectValue placeholder="Selecione o gênero do aluno" />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    <SelectItem value="MASCULINO">Masculino</SelectItem>
                    <SelectItem value="FEMININO">Feminino</SelectItem>
                  </SelectContent>
                </CustomSelect>
                <FormMessage className="text-xs">{form.formState.errors.sexo?.message}</FormMessage>
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="estadoCivil"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Estado Cívil</FormLabel>
                <CustomSelect onValueChange={field.onChange} defaultValue={field.value ?? ''}>
                  <FormControl>
                    <SelectTrigger>
                      <SelectValue placeholder="Selecione o Estado cívil do aluno" />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    <SelectItem value="SOLTEIRO">Solteiro</SelectItem>
                    <SelectItem value="CASADO">Casado</SelectItem>
                    <SelectItem value="DIVORCIADO">Divorciado</SelectItem>
                    <SelectItem value="VIUVO">Viúvo</SelectItem>
                    <SelectItem value="SEPARADO">Separado</SelectItem>
                    <SelectItem value="UNIAO_ESTAVEL">União Estável</SelectItem>
                  </SelectContent>
                </CustomSelect>
                <FormMessage className="text-xs">{form.formState.errors.estadoCivil?.message}</FormMessage>
              </FormItem>
            )}
          />
          <div className="col-span-2">
            <FormField
              control={form.control}
              name="consultor.id"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    {' '}
                    <span className="text-gray-400">*</span>Consultor
                  </FormLabel>
                  <FormControl>
                    <ComboSelectWithLabel
                      defaultValue={field.value}
                      data={dataConsult}
                      onChange={(e) => {
                        //console.log(e)
                        const id = e?.value
                        form.setValue('consultor.id', id)
                        form.setValue('consultor.nome', e?.label)
                        form.setValue('consultor.email', e?.email)
                        form.trigger(['consultor.id', 'consultor.nome', 'consultor.email'])
                      }}
                    />
                  </FormControl>
                  <FormMessage className="text-xs">{form.formState.errors?.consultor?.nome?.message}</FormMessage>
                </FormItem>
              )}
            />
          </div>
          <FormField
            control={form.control}
            name="profissao"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Profissão</FormLabel>
                <CustomSelect onValueChange={field.onChange} defaultValue={field.value ?? ''}>
                  <FormControl>
                    <SelectTrigger>
                      <SelectValue placeholder="Selecione Profissão do Aluno" />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    <SelectItem value="ADMINISTRADOR">Administrador</SelectItem>
                    <SelectItem value="ADVOGADO">Advogado</SelectItem>
                    <SelectItem value="ENGENHEIRO">Engenheiro</SelectItem>
                    <SelectItem value="MEDICO">Médico</SelectItem>
                    <SelectItem value="PROFESSOR">Professor</SelectItem>
                    <SelectItem value="ENFERMEIRO">Enfermeiro</SelectItem>
                    <SelectItem value="ARQUITETO">Arquiteto</SelectItem>
                    <SelectItem value="PROGRAMADOR">Programador</SelectItem>
                    <SelectItem value="Engenheiro de Software">Engenheiro de Software</SelectItem>
                    <SelectItem value="OUTRAS">Outras</SelectItem>
                  </SelectContent>
                </CustomSelect>
                <FormMessage className="text-xs">{form.formState.errors.profissao?.message}</FormMessage>
              </FormItem>
            )}
          />
        </div>
        <h3 className="font-bold mt-2">Contato</h3>
        <div className="grid grid-cols-2 gap-3 w-full">
          <FormField
            control={form.control}
            name="email"
            render={({ field }) => (
              <FormItem>
                <FormLabel>
                  <span className="text-gray-400">*</span> Email
                </FormLabel>
                <FormControl>
                  <Input {...field} placeholder="aluno@cash-fit.com" />
                </FormControl>
                <FormMessage className="text-xs">{form.formState.errors.email?.message}</FormMessage>
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name={'telefoneCelular.numero'}
            render={({ field }) => (
              <FormItem>
                <FormLabel>
                  <span className="text-gray-400">*</span> Celular
                </FormLabel>
                <FormControl>
                  <Input {...field} {...registerWithMask('telefoneCelular.numero', ['(99) 9999-9999', '(99) 99999-9999'])} placeholder="(00) [0]0000-0000" />
                </FormControl>
                <FormMessage className="text-xs">{form.formState.errors?.telefoneCelular?.numero?.message}</FormMessage>
              </FormItem>
            )}
          />

          <div className="col-span-2">
            <FormField
              control={form.control}
              name="telefoneResidencial.numero"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Residencial</FormLabel>
                  <FormControl>
                    <Input {...field} {...registerWithMask('telefoneResidencial.numero', ['(99) 9999-9999', '(99) 99999-9999'])} placeholder="(00) [0]0000-0000" />
                  </FormControl>
                  <FormMessage className="text-xs">{form.formState.errors?.telefoneResidencial?.numero?.message}</FormMessage>
                </FormItem>
              )}
            />
          </div>
        </div>
        <h3 className="font-bold mt-2">Contato Emergência</h3>
        <div className="grid grid-cols-2 gap-3 w-full">
          <FormField
            control={form.control}
            name="dadosEmergencia.contato"
            render={({ field }) => (
              <FormItem>
                <FormLabel>
                  <span className="text-gray-400"></span> Contato
                </FormLabel>
                <FormControl>
                  <Input {...field} placeholder="Contato..." />
                </FormControl>
                <FormMessage className="text-xs">{form.formState.errors?.dadosEmergencia?.contato?.message}</FormMessage>
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name={`dadosEmergencia.telefone`}
            render={({ field }) => (
              <FormItem>
                <FormLabel>
                  <span className="text-gray-400"></span> Telefone
                </FormLabel>
                <FormControl>
                  <Input {...field} {...registerWithMask('dadosEmergencia.telefone', ['(99) 9999-9999', '(99) 99999-9999'])} placeholder="(00) [0]0000-0000" />
                </FormControl>
                <FormMessage className="text-xs">{form.formState.errors?.dadosEmergencia?.telefone?.message}</FormMessage>
              </FormItem>
            )}
          />
        </div>
        <h3 className="font-bold mt-2">Endereço</h3>
        <div className="grid grid-cols-2 gap-3 w-full">
          <FormField
            control={form.control}
            name="endereco.cep"
            render={({ field }) => (
              <FormItem className="w-full">
                <FormLabel>
                  <span className="text-gray-400">*</span> Cep
                </FormLabel>
                <FormControl>
                  <Input {...field} {...registerWithMask('endereco.cep', '99999-999')} placeholder="00000-000" />
                </FormControl>
                <FormMessage className="text-xs">{form.formState.errors.endereco?.cep?.message}</FormMessage>
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="endereco.numero"
            render={({ field }) => (
              <FormItem className="w-full">
                <FormLabel>
                  <span className="text-gray-400">*</span> Numero
                </FormLabel>
                <FormControl>
                  <Input
                    {...field}
                    value={field.value || 'xxx'}
                    onChange={(e) => {
                      const numericValue = e.target.value.replace(/[^0-9]/g, '')
                      field.onChange(numericValue)
                    }}
                    placeholder="123"
                    type="number"
                    className="placeholder:text-gray-400 text-left direction-rtl"
                  />
                </FormControl>
                <FormMessage className="text-xs">{form.formState.errors.endereco?.numero?.message}</FormMessage>
              </FormItem>
            )}
          />
        </div>

        <div className="grid grid-cols-2 gap-3 w-full">
          <FormField
            control={form.control}
            name="endereco.bairro"
            render={({ field }) => (
              <FormItem className="w-full">
                <FormLabel className="">
                  <span className="text-gray-400">*</span> Bairro
                </FormLabel>
                <FormControl>
                  <Input {...field} placeholder="Centro" className="placeholder:text-gray-400" />
                </FormControl>
                <FormMessage className="text-xs">{form.formState.errors.endereco?.bairro?.message}</FormMessage>
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="endereco.cidade"
            render={({ field }) => (
              <FormItem>
                <FormLabel>
                  <span className="text-gray-400">*</span> Cidade
                </FormLabel>
                <FormControl>
                  <Input {...field} placeholder="Curitiba" className="placeholder:text-gray-400" />
                </FormControl>
                <FormMessage className="text-xs">{form.formState.errors.endereco?.cidade?.message}</FormMessage>
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="endereco.estado"
            render={({ field }) => (
              <FormItem>
                <FormLabel>
                  <span className="text-gray-400">*</span> Estado
                </FormLabel>
                <FormControl>
                  <ComboSelect defaultValue={field.value} data={states} onChange={field.onChange} />
                </FormControl>
                <FormMessage className="text-xs">{form.formState.errors.endereco?.estado?.message}</FormMessage>
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="endereco.rua"
            render={({ field }) => (
              <FormItem>
                <FormLabel>
                  <span className="text-gray-400">*</span> Rua
                </FormLabel>
                <FormControl>
                  <Input {...field} placeholder="rua" className="placeholder:text-gray-400" />
                </FormControl>
                <FormMessage className="text-xs">{form.formState.errors.endereco?.rua?.message}</FormMessage>
              </FormItem>
            )}
          />
        </div>

        <h3 className="font-bold mt-2">Responsável</h3>
        <div className="grid grid-cols-2 gap-3 w-full">
          <FormField
            control={form.control}
            name="responsavel.nome"
            render={({ field }) => (
              <FormItem>
                <FormLabel>
                  <span className="text-gray-400"></span> Nome
                </FormLabel>
                <FormControl>
                  <Input {...field} placeholder="Nome..." />
                </FormControl>
                <FormMessage className="text-xs">{form.formState.errors?.responsavel?.nome?.message}</FormMessage>
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="responsavel.telefone"
            render={({ field }) => (
              <FormItem>
                <FormLabel>
                  <span className="text-gray-400"></span> Telefone
                </FormLabel>
                <FormControl>
                  <Input {...field} {...registerWithMask('responsavel.telefone', ['(99) 9999-9999', '(99) 99999-9999'])} placeholder="Telefone..." />
                </FormControl>
                <FormMessage className="text-xs">{form.formState.errors?.responsavel?.telefone?.message}</FormMessage>
              </FormItem>
            )}
          />
          <div className="col-span-2">
            <FormField
              control={form.control}
              name={`responsavel.documento`}
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    <span className="text-gray-400"></span> CPF
                  </FormLabel>
                  <FormControl>
                    <Input
                      {...field}
                      {...registerWithMask('responsavel.documento', 'cpf', {
                        required: true
                      })}
                      placeholder="000.000.000-00"
                    />
                  </FormControl>
                  <FormMessage className="text-xs">{form.formState.errors?.responsavel?.documento?.message}</FormMessage>
                </FormItem>
              )}
            />
          </div>
        </div>
        <h3 className="font-bold mt-2">Observação</h3>
        <FormField
          control={form.control}
          name="observacao"
          render={({ field }) => (
            <FormItem>
              <FormControl>
                <Textarea {...field} placeholder="Observações..." />
              </FormControl>
              <FormMessage className="text-xs">{form.formState.errors?.observacao?.message}</FormMessage>
            </FormItem>
          )}
        />
        <h3 className="font-bold mt-2">Informações Adicionais</h3>
        <div className="grid grid-cols-2 gap-3 w-full">
          <FormField
            control={form.control}
            name="informacoesAdicionais.objetivoPrincipal"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Objetivo Principal</FormLabel>
                <FormControl>
                  <Input {...field} placeholder="Objetivo Principal..." />
                </FormControl>
                <FormMessage className="text-xs">{form.formState.errors?.informacoesAdicionais?.objetivoPrincipal?.message}</FormMessage>
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="informacoesAdicionais.observacoes"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Observações</FormLabel>
                <FormControl>
                  <Input {...field} placeholder="Observações..." />
                </FormControl>
                <FormMessage className="text-xs">{form.formState.errors?.informacoesAdicionais?.observacoes?.message}</FormMessage>
              </FormItem>
            )}
          />
          {selectedModalities && (
            <div className="col-span-2">
              <Select
                isMulti
                options={modality}
                className="basic-multi-select w-full  h-fit border-black"
                styles={{
                  valueContainer(base: any) {
                    return {
                      ...base,
                      overflowY: 'hidden',
                      height: 36
                    }
                  }
                }}
                defaultValue={selectedModalities || []}
                classNamePrefix="select"
                value={selectedModalities}
                onChange={(e: any) => {
                  setSelectedModalities(e as any)
                }}
                placeholder="Selecione as modalidades de interesse"
              />
            </div>
          )}
        </div>

        {/* <h3 className="font-bold mt-2">Responsabilidade</h3>
        <div className="grid grid-cols-2 gap-3 w-full">
          <FormField
            control={form.control}
            name="responsabilidade.indicacao"
            render={({ field }) => (
              <FormItem>
                <FormLabel>
                  <span className="text-gray-400"></span> Indicação
                </FormLabel>
                <FormControl>
                  <Input {...field} placeholder="Indicação..." />
                </FormControl>
                <FormMessage className="text-xs">{form.formState.errors?.responsabilidade?.indicacao?.message}</FormMessage>
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="responsabilidade.consultor"
            render={({ field }) => (
              <FormItem>
                <FormLabel>
                  <span className="text-gray-400"></span> Consultor
                </FormLabel>
                <FormControl>
                  <Input {...field} placeholder="Consultor..." />
                </FormControl>
                <FormMessage className="text-xs">{form.formState.errors?.responsabilidade?.consultor?.message}</FormMessage>
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name={`responsabilidade.professor`}
            render={({ field }) => (
              <FormItem>
                <FormLabel>
                  <span className="text-gray-400"></span> Professor
                </FormLabel>
                <FormControl>
                  <Input {...field} placeholder="Professor..." />
                </FormControl>
                <FormMessage className="text-xs">{form.formState.errors?.responsabilidade?.professor?.message}</FormMessage>
              </FormItem>
            )}
          />
        </div> */}
        <Button type="submit" className="bg-acelerafit-green-400 text-muted  font-bold hover:bg-acelerafit-green-50 mt-3">
          Enviar
        </Button>
      </form>
    </Form>
  )
}
